/**
 * Membership checkout: Razorpay (web/Android) vs Apple IAP via RevenueCat (iOS native).
 */

import axios from 'axios';
import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import { useAuthStore } from '@/stores/authStore';
import { storageManager } from '@/storage/storageManager';
import { STORAGE_KEYS } from '@/storage/storageKeys';
import { getAuthCookies } from '@/utils/cookies';
import { getNativeAccessTokenFromCookies } from '@/utils/nativeAuthCookies';
import { getRcPackageIdForBackendPlan } from '@/data/iapProductMapping';
import {
  executeNativeIapPurchase,
  runIapPrepare,
  runIapSyncBestEffort,
} from '@/services/iapPurchaseFlow';
import { PaymentService } from '@/services/paymentService';
import {
  getActiveEntitlementIds,
  isRevenueCatEnabled,
  restorePurchases,
} from '@/services/revenueCatService';
import { isNativeIOSApp } from '@/utils/platform';
import type { CustomerInfo } from '@revenuecat/purchases-capacitor';

export type MembershipPurchaseMode = 'full' | 'emi';

export interface MembershipCheckoutParams {
  backendPlanId: string;
  categoryName?: string | null;
  mode: MembershipPurchaseMode;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  emiAmountInr?: number | null;
  fullPriceInr: number;
  appUserId: string;
  /** Dismiss web overlays (e.g. toast) before the App Store sheet appears. */
  onBeforeStoreKit?: () => void;
  onSuccess: () => void | Promise<void>;
  onFailure: (error: { code?: string; description?: string }) => void;
}

interface RazorpayBuyResponse {
  subscription_id: string;
  subscription_name: string;
  description: string;
  short_url?: string;
  status: string;
}

type MembershipLinkResponse = {
  success?: boolean;
  data?: {
    status?: string;
    login_link?: string;
    short_url?: string;
  };
};

async function createRazorpaySubscription(
  backendPlanId: string,
  paymentType: 'emi' | 'subscription',
): Promise<RazorpayBuyResponse> {
  const response = await apiClient.post(API_ENDPOINTS.PLANS.BUY, {
    plan_id: backendPlanId,
    payment_type: paymentType,
  });
  const data = response.data as RazorpayBuyResponse;
  if (!data?.subscription_id) {
    throw new Error('Subscription ID not received from server');
  }
  return data;
}

function buildMembershipSyncPayload(
  backendPlanId: string,
  appUserId: string,
  productIdentifier: string,
  rcPackageIdentifier: string,
  customerInfo: CustomerInfo,
) {
  return {
    plan_id: backendPlanId,
    payment_type: 'iap' as const,
    revenuecat_app_user_id: appUserId,
    product_identifier: productIdentifier,
    rc_package_identifier: rcPackageIdentifier,
    active_entitlement_ids: getActiveEntitlementIds(customerInfo),
    original_app_user_id: customerInfo.originalAppUserId,
    management_url: customerInfo.managementURL ?? null,
  };
}

export function membershipCheckoutUsesIap(): boolean {
  return isNativeIOSApp() && isRevenueCatEnabled();
}

export function membershipEmiAvailable(): boolean {
  return !isNativeIOSApp();
}

const MEMBERSHIP_WORKFLOW_LOG = '[MembershipWorkflowLink]';

type WorkflowAccessTokenSource =
  | 'explicit'
  | 'authStore'
  | 'secureStorage'
  | 'nativeCookie'
  | 'cookie'
  | 'cookieSession'
  | 'none';

type WorkflowTokenDiagnostics = {
  accessToken: string | null;
  secureStorageToken: string | null;
  nativeCookieUrl: string | null;
  nativeCookieKeys: string[];
  readableCookieAccess: string | null;
};

function hasAuthenticatedAppSession(): boolean {
  return useAuthStore.getState().isAuthenticated === true;
}

async function buildWorkflowTokenDiagnostics(
  accessToken: string | null,
  secureStorageToken: string | null = null,
): Promise<WorkflowTokenDiagnostics> {
  const nativeCookie = await getNativeAccessTokenFromCookies();
  return {
    accessToken,
    secureStorageToken,
    nativeCookieUrl: nativeCookie.cookieUrl,
    nativeCookieKeys: nativeCookie.cookieKeys,
    readableCookieAccess: getAuthCookies().access?.trim() || null,
  };
}

async function resolveAccessTokenForWorkflowLink(
  explicitToken?: string,
): Promise<{
  token: string | null;
  source: WorkflowAccessTokenSource;
  diagnostics: WorkflowTokenDiagnostics;
}> {
  const trimmedExplicit = explicitToken?.trim() || null;
  if (trimmedExplicit) {
    return {
      token: trimmedExplicit,
      source: 'explicit',
      diagnostics: await buildWorkflowTokenDiagnostics(trimmedExplicit),
    };
  }

  const storeToken = useAuthStore.getState().accessToken?.trim() || null;
  if (storeToken) {
    return {
      token: storeToken,
      source: 'authStore',
      diagnostics: await buildWorkflowTokenDiagnostics(storeToken),
    };
  }

  const hydratedToken = await useAuthStore.getState().hydrateAccessTokenFromSecureStorage();
  if (hydratedToken) {
    return {
      token: hydratedToken,
      source: 'secureStorage',
      diagnostics: await buildWorkflowTokenDiagnostics(hydratedToken, hydratedToken),
    };
  }

  const nativeCookie = await getNativeAccessTokenFromCookies();
  if (nativeCookie.token) {
    useAuthStore.setState({ accessToken: nativeCookie.token });
    return {
      token: nativeCookie.token,
      source: 'nativeCookie',
      diagnostics: {
        accessToken: nativeCookie.token,
        secureStorageToken: null,
        nativeCookieUrl: nativeCookie.cookieUrl,
        nativeCookieKeys: nativeCookie.cookieKeys,
        readableCookieAccess: getAuthCookies().access?.trim() || null,
      },
    };
  }

  const readableCookieAccess = getAuthCookies().access?.trim() || null;
  if (readableCookieAccess) {
    useAuthStore.setState({ accessToken: readableCookieAccess });
    return {
      token: readableCookieAccess,
      source: 'cookie',
      diagnostics: {
        accessToken: readableCookieAccess,
        secureStorageToken: null,
        nativeCookieUrl: nativeCookie.cookieUrl,
        nativeCookieKeys: nativeCookie.cookieKeys,
        readableCookieAccess,
      },
    };
  }

  const secureStorageTokenRaw = await storageManager.get<string>(STORAGE_KEYS.ACCESS_TOKEN, true);
  const storedToken = typeof secureStorageTokenRaw === 'string' ? secureStorageTokenRaw.trim() : null;

  return {
    token: null,
    source: 'none',
    diagnostics: {
      accessToken: null,
      secureStorageToken: storedToken,
      nativeCookieUrl: nativeCookie.cookieUrl,
      nativeCookieKeys: nativeCookie.cookieKeys,
      readableCookieAccess,
    },
  };
}

function logMembershipWorkflowRequest(
  requestId: string,
  endpoint: string,
  payload: Record<string, never>,
  accessToken: string | null,
  tokenSource: WorkflowAccessTokenSource,
  tokenDiagnostics: WorkflowTokenDiagnostics,
): void {
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} >>> REQUEST`);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} method:`, 'POST');
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} url:`, endpoint);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} payload:`, payload);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} withCredentials:`, true);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} tokenSource:`, tokenSource);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} accessToken:`, accessToken);
  console.log(
    `${MEMBERSHIP_WORKFLOW_LOG} ${requestId} Authorization:`,
    accessToken ? `Bearer ${accessToken}` : null,
  );
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} isAuthenticated:`, hasAuthenticatedAppSession());
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} nativeCookieUrl:`, tokenDiagnostics.nativeCookieUrl);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} nativeCookieKeys:`, tokenDiagnostics.nativeCookieKeys);
  console.log(
    `${MEMBERSHIP_WORKFLOW_LOG} ${requestId} readableCookieAccess:`,
    tokenDiagnostics.readableCookieAccess,
  );
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} tokenDiagnostics:`, tokenDiagnostics);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} authCookies:`, getAuthCookies());
}

type MembershipWorkflowRedirectChoice = {
  loginLink: string | null;
  shortUrl: string | null;
  chosenRedirectUrl: string | null;
  chosenRedirectType: 'login_link' | 'short_url' | null;
};

function logMembershipWorkflowResponse(
  requestId: string,
  status: number,
  headers: unknown,
  data: MembershipLinkResponse | undefined,
  redirectChoice: MembershipWorkflowRedirectChoice,
): void {
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} <<< RESPONSE`);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} status:`, status);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} headers:`, headers);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} data:`, data);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} login_link:`, redirectChoice.loginLink);
  console.log(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} short_url:`, redirectChoice.shortUrl);
  console.log(
    `${MEMBERSHIP_WORKFLOW_LOG} ${requestId} chosenRedirectUrl:`,
    redirectChoice.chosenRedirectUrl,
  );
  console.log(
    `${MEMBERSHIP_WORKFLOW_LOG} ${requestId} chosenRedirectType:`,
    redirectChoice.chosenRedirectType,
  );
}

function logMembershipWorkflowSummary(
  requestId: string,
  endpoint: string,
  payload: Record<string, never>,
  tokenSource: WorkflowAccessTokenSource,
  accessToken: string | null,
  status: number,
  redirectChoice: MembershipWorkflowRedirectChoice,
  tokenDiagnostics: WorkflowTokenDiagnostics,
): void {
  console.log(
    `${MEMBERSHIP_WORKFLOW_LOG} ${requestId} summary`,
    JSON.stringify({
      url: endpoint,
      payload,
      tokenSource,
      authorization: accessToken ? `Bearer ${accessToken.slice(0, 12)}...` : 'cookie-session',
      nativeCookieUrl: tokenDiagnostics.nativeCookieUrl,
      nativeCookieKeys: tokenDiagnostics.nativeCookieKeys,
      readableCookieAccess: tokenDiagnostics.readableCookieAccess,
      responseStatus: status,
      login_link: redirectChoice.loginLink,
      short_url: redirectChoice.shortUrl,
      chosenRedirectUrl: redirectChoice.chosenRedirectUrl,
      chosenRedirectType: redirectChoice.chosenRedirectType,
    }),
  );
}

function logMembershipWorkflowError(requestId: string, error: unknown): void {
  console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} <<< ERROR`);
  if (axios.isAxiosError(error)) {
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} type:`, 'AxiosError');
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} message:`, error.message);
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} code:`, error.code);
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} status:`, error.response?.status);
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} responseHeaders:`, error.response?.headers);
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} responseData:`, error.response?.data);
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} requestMethod:`, error.config?.method);
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} requestUrl:`, error.config?.url);
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} requestData:`, error.config?.data);
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} requestHeaders:`, error.config?.headers);
    return;
  }
  if (error instanceof Error) {
    // WKWebView / Xcode often prints `Error` as `{}` — log message explicitly.
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} type:`, 'Error');
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} message:`, error.message);
    console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} name:`, error.name);
    if (error.stack) {
      console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} stack:`, error.stack);
    }
    return;
  }
  console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} type:`, typeof error);
  console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} raw:`, JSON.stringify(error));
}

function getMembershipWorkflowLinkErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    return 'Your session has expired. Please sign in again, then retry.';
  }
  if (error instanceof Error) return error.message;
  return 'Could not generate website redirect link.';
}

/** iOS: create one-time membership login link (POST workflow-short-link). */
export async function fetchMembershipWebsiteLinkByPhone(
  explicitToken?: string,
): Promise<string> {
  const requestId = `req-${Date.now()}`;
  const endpoint = API_ENDPOINTS.PLANS.ONE_TIME_LOGIN_LINK_BY_PHONE_FOR_MEMBERSHIP;
  const payload = {} as Record<string, never>;

  try {
    if (!useAuthStore.getState().accessToken) {
      await useAuthStore.getState().hydrateAccessTokenFromSecureStorage();
    }

    const { token: resolvedToken, source: resolvedSource, diagnostics: tokenDiagnostics } =
      await resolveAccessTokenForWorkflowLink(explicitToken);

    let accessToken: string | null = resolvedToken;
    let tokenSource: WorkflowAccessTokenSource = resolvedSource;

    if (!accessToken) {
      if (hasAuthenticatedAppSession()) {
        tokenSource = 'cookieSession';
        console.warn(
          `${MEMBERSHIP_WORKFLOW_LOG} ${requestId} no Bearer token; using cookie session`,
          tokenDiagnostics,
        );
      } else {
        console.error(`${MEMBERSHIP_WORKFLOW_LOG} ${requestId} no access token`, tokenDiagnostics);
        throw new Error('You are not logged in. Please sign in again and retry.');
      }
    }

    logMembershipWorkflowRequest(
      requestId,
      endpoint,
      payload,
      accessToken,
      tokenSource,
      tokenDiagnostics,
    );

    const response = await apiClient.post<MembershipLinkResponse>(
      endpoint,
      payload,
      {
        withCredentials: true,
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      },
    );

    const body = response.data ?? {};
    const shortUrl = body.data?.short_url?.trim() || null;
    const loginLink = body.data?.login_link?.trim() || null;
    const chosenRedirectType: MembershipWorkflowRedirectChoice['chosenRedirectType'] = loginLink
      ? 'login_link'
      : shortUrl
        ? 'short_url'
        : null;
    const redirectUrl = loginLink || shortUrl || null;

    const redirectChoice: MembershipWorkflowRedirectChoice = {
      loginLink,
      shortUrl,
      chosenRedirectUrl: redirectUrl,
      chosenRedirectType,
    };

    logMembershipWorkflowResponse(requestId, response.status, response.headers, body, redirectChoice);
    logMembershipWorkflowSummary(
      requestId,
      endpoint,
      payload,
      tokenSource,
      accessToken,
      response.status,
      redirectChoice,
      tokenDiagnostics,
    );

    if (!body.success || !redirectUrl) {
      throw new Error('Redirect URL was not returned by membership link API.');
    }

    return redirectUrl;
  } catch (error) {
    logMembershipWorkflowError(requestId, error);
    throw new Error(getMembershipWorkflowLinkErrorMessage(error));
  }
}

async function checkoutWithRazorpay(params: MembershipCheckoutParams): Promise<void> {
  const paymentType = params.mode === 'emi' ? 'emi' : 'subscription';
  const data = await createRazorpaySubscription(params.backendPlanId, paymentType);

  if (params.mode === 'emi') {
    const amountInPaise = (params.emiAmountInr ?? params.fullPriceInr) * 100;
    void PaymentService.openCheckout({
      amount: amountInPaise,
      orderId: data.subscription_id,
      name: params.customerName,
      description: data.description || `Membership EMI - ${data.subscription_name}`,
      email: params.customerEmail,
      phone: params.customerPhone,
      razorpayOptions: {
        method: {
          card: true,
          netbanking: true,
          wallet: false,
          upi: false,
          emi: true,
          paylater: false,
        },
      },
      onSuccess: async () => {
        await params.onSuccess();
      },
      onFailure: params.onFailure,
    });
    return;
  }

  void PaymentService.openSubscriptionCheckout({
    subscriptionId: data.subscription_id,
    name: params.customerName,
    description: data.description || `Membership - ${data.subscription_name}`,
    email: params.customerEmail,
    phone: params.customerPhone,
    onSuccess: async () => {
      await params.onSuccess();
    },
    onFailure: params.onFailure,
  });
}

async function checkoutWithIap(params: MembershipCheckoutParams): Promise<void> {
  if (params.mode === 'emi') {
    params.onFailure({
      code: 'IAP_EMI_NOT_SUPPORTED',
      description: 'EMI is not available on iOS. Use Subscribe with Apple.',
    });
    return;
  }

  const rcPackageId = getRcPackageIdForBackendPlan(
    params.backendPlanId,
    params.categoryName,
  );
  if (!rcPackageId) {
    throw new Error(
      'No App Store package mapped for this plan. Check VITE_RC_PKG_* env variables.',
    );
  }

  await runIapPrepare(API_ENDPOINTS.PLANS.IAP_PREPARE, {
    plan_id: params.backendPlanId,
    payment_type: 'iap',
  });

  const purchase = await executeNativeIapPurchase({
    appUserId: params.appUserId,
    rcPackageId,
    onBeforeStoreKit: params.onBeforeStoreKit,
  });

  await runIapSyncBestEffort(
    API_ENDPOINTS.PLANS.IAP_SYNC,
    buildMembershipSyncPayload(
      params.backendPlanId,
      params.appUserId,
      purchase.productIdentifier,
      purchase.rcPackageIdentifier,
      purchase.customerInfo,
    ),
    purchase.customerInfo,
  );

  await params.onSuccess();
}

export async function purchaseMembership(
  params: MembershipCheckoutParams,
): Promise<void> {
  if (isNativeIOSApp() && params.mode === 'emi') {
    params.onFailure({
      code: 'IAP_EMI_NOT_SUPPORTED',
      description: 'EMI is not available on iOS. Use Subscribe with Apple.',
    });
    return;
  }

  if (membershipCheckoutUsesIap()) {
    await checkoutWithIap(params);
    return;
  }
  await checkoutWithRazorpay(params);
}

export async function restoreMembershipPurchases(appUserId: string): Promise<void> {
  const customerInfo = await restorePurchases(appUserId);
  await runIapSyncBestEffort(
    API_ENDPOINTS.PLANS.IAP_RESTORE,
    {
      revenuecat_app_user_id: appUserId,
      active_entitlement_ids: getActiveEntitlementIds(customerInfo),
      original_app_user_id: customerInfo.originalAppUserId,
      management_url: customerInfo.managementURL ?? null,
    },
    customerInfo,
  );
}
